import { useEffect, useRef, useContext } from 'react';
import { LoadingContext } from '../App';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Canvas from '../components/Canvas';
import MainFooter from '../components/MainFooter';
import portfolioData from '../data/portfolio.json';
import blogData from '../data/blog.json';
import directoryData from '../data/blogDirectories.json';

import '../styles/our-work.css';
import '../styles/blog-directory.css';

function OurWork() {
    const location = useLocation();
    const portfolioTrackRef = useRef<HTMLDivElement>(null);
    const blogsTrackRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Update tab name handled by Helmet

        // linkRef cleanup is no longer needed

        return () => {
            // No cleanup needed for static CSS
        };
    }, []);

    const { setIsPageReady } = useContext(LoadingContext);

    // Scroll to top when component mounts
    useEffect(() => {
        // The centralized LoadScript handles most scroll resets, 
        // but we keep a simple immediate reset here as a safety measure.
        window.scrollTo(0, 0);

        // Signal that the page is ready
        setIsPageReady(true);
    }, [setIsPageReady]);

    // Portfolio data from JSON
    const portfolioItems = portfolioData;

    // Blog data from JSON
    const blogItems = blogData;

    const scrollSlider = (trackRef: React.RefObject<HTMLDivElement>, direction: 'left' | 'right') => {
        if (trackRef.current) {
            // Get the track element (first child of the wrapper)
            const track = trackRef.current.firstElementChild as HTMLElement;
            if (track && track.firstElementChild) {
                const firstCard = track.firstElementChild as HTMLElement;
                const cardWidth = firstCard.offsetWidth;
                const gap = 30; // Gap between cards from CSS
                const scrollAmount = (cardWidth + gap) * 3;

                trackRef.current.scrollBy({
                    left: direction === 'left' ? -scrollAmount : scrollAmount,
                    behavior: 'smooth'
                });
            } else {
                // Fallback for unexpected structure
                const scrollAmount = 1200;
                trackRef.current.scrollBy({
                    left: direction === 'left' ? -scrollAmount : scrollAmount,
                    behavior: 'smooth'
                });
            }
        }
    };

    return (
        <>
            <Helmet>
                <title>Our Work | Portfolio & Insights | GoHighLevel & CRM Automation | Sacesta</title>
                <meta name="description" content="Explore Sacesta Technologies' portfolio of creative projects, web developments, and insightful blogs on technology, innovation, and GoHighLevel CRM Automation." />
                <meta name="keywords" content="Sacesta portfolio, web developments, technology and innovation, GoHighLevel, CRM automation, Ahmedabad agency" />
                <link rel="canonical" href="https://sacesta.com/our-work" />

                {/* FB / WhatsApp sharing */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://sacesta.com/our-work" />
                <meta property="og:title" content="Our Work | Portfolio & Insights | Sacesta" />
                <meta property="og:description" content="Explore our latest projects and technical insights. From web design to GHL automation." />
                <meta property="og:image" content="https://sacesta.com/assets/imgs/logo/site-logo-white-2.png" />
            </Helmet>
            <div className="cursor1"></div>
            <div className="cursor2"></div>

            <div className="has-smooth" id="has_smooth"></div>

            <button id="scroll_top" className="scroll-top"><i className="fa-solid fa-arrow-up"></i></button>

            <header className="header__area-3">
                <div className="header__inner-3">
                    <div className="header__logo-2">
                        <Link to="/" className="logo-dark">
                            <img src="assets/imgs/logo/sacesta-logo-black.png" style={{ width: '150px', height: '40px' }} alt="Site Logo" />
                        </Link>
                        <Link to="/" className="logo-light">
                            <img src="assets/imgs/logo/site-logo-white-2.png" alt="Site Logo" />
                        </Link>
                    </div>
                    <div className="header__nav-2">
                        <ul className="main-menu-3 menu-anim">
                            <li>
                                <Link to="/">Home</Link>
                            </li>
                            <li>
                                <Link to="/about">About</Link>
                            </li>
                            <li className="has-megamenu">
                                <Link to="/our-work">Our Work</Link>
                            </li>
                            <li><a href="https://calendly.com/sacesta/30min">Book A Meet</a></li>
                        </ul>
                    </div>
                    <div className="header__nav-icon-3">
                        <button className="search-icon" id="search_icon"><i className="fa-solid fa-magnifying-glass"></i></button>
                        <button className="search-icon" id="search_close"><i className="fa-solid fa-xmark"></i></button>
                        <button id="open_offcanvas"><img src="assets/imgs/icon/menu-black.png" alt="Menubar Icon" /></button>
                    </div>
                </div>
            </header>

            <div className="header__search">
                <form action="#">
                    <input type="text" name="s" id="s" placeholder="Search.." />
                </form>
            </div>

            <Canvas />

            <div id="smooth-wrapper">
                <div id="smooth-content">
                    <div className="see-our-work-page">
                        <main>
                            {/* Portfolio Section */}
                            <section className="portfolio-scroll-section">
                                <div className="container">
                                    <div className="section-header">
                                        <h2>Portfolio</h2>
                                        <p>Explore our latest projects and creative work</p>
                                    </div>
                                    <div className="slider-container">
                                        <button className="slider-btn prev" onClick={() => scrollSlider(portfolioTrackRef, 'left')}>
                                            <i className="fa-solid fa-arrow-left"></i>
                                        </button>
                                        <div className="portfolio-scroll-wrapper" ref={portfolioTrackRef}>
                                            <div className="portfolio-scroll-track manual">
                                                {portfolioItems.map((item: any, index: number) => (
                                                    <Link key={`portfolio-${index}`} to={`/our-work/portfolio/${item.projectId}`} className="portfolio-card">
                                                        <div className="portfolio-card-image">
                                                            <img src={item.hero.heroImage.suggested} alt={item.title} />
                                                        </div>
                                                        <div className="portfolio-card-content">
                                                            <h3>{item.hero.mainTitle}</h3>
                                                            <p>{item.hero.tagline}</p>
                                                            <div className="portfolio-card-date">{item.metadata.date}</div>
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                        <button className="slider-btn next" onClick={() => scrollSlider(portfolioTrackRef, 'right')}>
                                            <i className="fa-solid fa-arrow-right"></i>
                                        </button>
                                    </div>
                                </div>
                            </section>

                            {/* Directory Section */}
                            <section className="blogs-scroll-section" style={{ background: '#fff' }}>
                                <div className="container">
                                    <div className="section-header">
                                        <h2 className="title-anim">Explore by Platform</h2>
                                        <p>Deep dives into the systems we build and master</p>
                                    </div>
                                    <div className="directory-grid">
                                        {directoryData && directoryData.length > 0 ? directoryData.map((dir, i) => (
                                            <Link 
                                                key={i} 
                                                to={`/our-work/blog/directory/${dir.slug}`}
                                                className="directory-card"
                                            >
                                                <h3>{dir.title}</h3>
                                                <div className="view-link">
                                                    View Directory <i className="fa-solid fa-arrow-right"></i>
                                                </div>
                                            </Link>
                                        )) : <p>No platforms found.</p>}
                                    </div>
                                </div>
                            </section>

                            {/* Blogs Section */}
                            <section className="blogs-scroll-section" id="blogs">
                                <div className="container">
                                    <div className="section-header">
                                        <h2>Blogs</h2>
                                        <p>Read our latest insights and articles</p>
                                    </div>
                                    <div className="slider-container">
                                        <button className="slider-btn prev" onClick={() => scrollSlider(blogsTrackRef, 'left')}>
                                            <i className="fa-solid fa-arrow-left"></i>
                                        </button>
                                        <div className="blogs-scroll-wrapper" ref={blogsTrackRef}>
                                            <div className="blogs-scroll-track manual">
                                                {blogItems.map((item, index) => (
                                                    <Link
                                                        key={`blog-${index}`}
                                                        to={`/our-work/blog/${item.slug}`}
                                                        state={{ from: location.pathname }}
                                                        className="blog-card"
                                                    >
                                                        <div className="blog-card-image">
                                                            <img src={`/${item.thumbnail.image}`} alt={item.thumbnail.alt} />
                                                        </div>
                                                        <div className="blog-card-content">
                                                            <div className="blog-card-meta">{item.date}</div>
                                                            <h3>{item.title}</h3>
                                                            <div className="blog-card-link">
                                                                Read More <i className="fa-solid fa-arrow-right"></i>
                                                            </div>
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                        <button className="slider-btn next" onClick={() => scrollSlider(blogsTrackRef, 'right')}>
                                            <i className="fa-solid fa-arrow-right"></i>
                                        </button>
                                    </div>
                                </div>
                            </section>
                        </main>

                        <MainFooter />
                    </div>
                </div>
            </div>
        </>
    );
}

export default OurWork;
